import React, { Fragment, useRef, useState } from 'react';
import { SearchOutlined, EditOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { filter, forEach, isNil, isNumber } from 'lodash';
import cx from 'classnames';
import ReactPlayer from 'react-player';
import { useDispatch } from 'react-redux';
import { AiOutlineFileImage, AiOutlineVideoCamera } from 'react-icons/ai';
import { asyncSimpleModal, getImgInfo, reOrderArray } from '../../lib/common';
import Img from '../Img';
import Anchor from '../link/Anchor';
import Modal from '../Modal';
import Badge from '../Badge';
import DragSortListWrap from '../DragSortListWrap';
import ReactCropper from '../ReactCropper';
import { PRODUCT_ATTACHMENT_TYPE } from '../../constants/service/app/productConstants';
import { appActions } from '../../store/modules/app';
import './ImgUpload.scss';

const ImgUpload = (props) => {
  const {
    list,
    maxCnt = 1,
    minCnt,
    modalTitle,
    multiple,
    isEditable = true,
    canAddVideo = false,
    rotatable = false,
    onlyVideo = false,
    setData,
    targetSize,
    enableGif,
    cropMode,
    restriction,
    targetPath,
    isProductAttachment = true,
    hideComponentError = false,
    capacityLimit,
    forceCrop = false,
  } = props;

  const [state, setState] = useState({
    src: null,
    file: null,
  });
  const [indexToUpdate, setIndexToUpdate] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const inputRef = useRef(null);
  const cropperRef = useRef(null);
  const { appToggleModal, addTempImg: saveImg } = appActions;
  const dispatch = useDispatch();

  /**
   * 이미지 순서 변경하기
   */
  const onDragEnd = (result) => {
    if (!isEditable) return;

    if (!result.destination || !setData) {
      return;
    }

    const newOrderedlist = reOrderArray(list, result.source.index, result.destination.index);

    setData({
      path: targetPath,
      value: newOrderedlist,
    });
  };
  /**
   * 업로드 버튼 초기화 하기
   */
  const clearUploadBtn = () => {
    //---> input file value 초기화(같은 이미지를 연속으로 업로드 할 수 있도록 함)
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  /**
   * 파일 타입이 유효한지
   */
  const isValidFileType = (file) => {
    //---> gif 업로드가 가능할 경우
    if (enableGif && file.name.toLowerCase().endsWith('gif') && /(image\/gif)$/i.test(file.type)) {
      return true;
    }

    if (
      !file.name.toLowerCase().endsWith('jpg') &&
      !file.name.toLowerCase().endsWith('jpeg') &&
      !file.name.toLowerCase().endsWith('png')
    ) {
      return false;
    }
    //---> file 형식 체크
    if (!/(image\/jpg|image\/jpeg|image\/png)$/i.test(file.type)) {
      return false;
    }
    return true;
  };

  /**
   * 이미지 선택시 불러오는 callback
   */
  const onChange = async (e) => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    //---> 파일 존재 체크
    if (!files) return false;
    //--> 상세 이미지 개수 체크
    if (isNil(indexToUpdate) && list.length + files.length > maxCnt) {
      const exceedLength = maxCnt - list.length;
      await asyncSimpleModal({
        type: 'error',
        title: '업로드 수',
        message: `업로드하려는 파일 수가 너무 많습니다.\n ${exceedLength}개 이하로 업로드 해주세요.`,
      });
      clearUploadBtn();
      return false;
    }

    forEach(files, async (file, uploadIndex) => {
      // 파일명 체크
      if (!isValidFileType(file)) {
        await asyncSimpleModal({
          type: 'error',
          title: '이미지 형식',
          message: '이미지 형식의 파일을 선택하십시오.',
        });
        clearUploadBtn();
        return false;
      }

      //---> file 사이즈 체크 1MB 이하만 받기
      let limitSize = 1024 * 1024;
      let limitText = '1MB';
      //---> 일반 이미지는 20mb 이하만 받기
      if (capacityLimit && isNumber(capacityLimit)) {
        limitSize = 1024 * 1024 * capacityLimit;
        limitText = `${capacityLimit}MB`;
      } else if (!cropMode) {
        limitSize = 1024 * 1024 * 20;
        limitText = '20MB';
      }

      if (file.size > limitSize) {
        await asyncSimpleModal({
          type: 'error',
          title: '이미지 사이즈',
          message: `이미지용량은 ${limitText}미만으로 제한합니다.`,
        });
        clearUploadBtn();
        return false;
      }

      try {
        /**
         *  이미지 정보 가져오기
         */
        const result = await getImgInfo(file);

        /**
         *  사이즈(너비, 높이) 검사
         */
        if (restriction) {
          const { widthMin, heightMin, widthMax, heightMax, width, height, square = false } = restriction;

          let isInvalid = false;
          let isNotExactWidth = false;
          let isNotExactHeight = false;

          if (square && result.width / result.height !== 1) {
            await asyncSimpleModal({
              type: 'error',
              title: '정방형 여부',
              message: `정방형 이미지만 업로드가 가능합니다.`,
            });
            isInvalid = true;
          }

          //---> 너비(최소)의 제한이 있을경우
          if (widthMin && widthMin > result.width) {
            await asyncSimpleModal({
              type: 'error',
              title: '업로드 이미지 가로 너비 제한',
              message: `너비 ${widthMin} 픽셀 이하의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다.`,
            });
            isInvalid = true;
          }

          //---> 너비(최대)의 제한이 있을경우
          else if (widthMax && widthMax < result.width) {
            await asyncSimpleModal({
              type: 'error',
              title: '업로드 이미지 가로 너비 제한',
              message: `너비 ${widthMax} 픽셀 이상의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다.`,
            });
            isInvalid = true;
          }

          //---> 높이(최소)의 제한이 있을경우
          else if (heightMin && heightMin > result.height) {
            await asyncSimpleModal({
              type: 'error',
              title: '업로드 이미지 세로 높이 제한',
              message: `높이 ${heightMin} 픽셀 이하의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다.`,
            });
            isInvalid = true;
          }

          //---> 높이(최대)의 제한이 있을경우
          else if (heightMax && heightMax < result.height) {
            await asyncSimpleModal({
              type: 'error',
              title: '업로드 이미지 세로 높이 제한',
              message: `높이 ${heightMax} 픽셀 이상의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다. 최대 ${heightMax} 픽셀 기준으로 이미지를 나눠서 올려주세요.`,
            });
            isInvalid = true;
          }

          //---> 정확한 너비의 제한이 있을경우
          if (width && width !== result.width) {
            isNotExactWidth = true;
            isInvalid = true;
          }

          //---> 정확한 높이의 제한이 있을경우
          if (height && height !== result.height) {
            isNotExactHeight = true;
            isInvalid = true;
          }

          /**
           *  정확한 너비, 높이가 맞지 않았을 경우
           */
          if (isNotExactWidth && isNotExactHeight) {
            await asyncSimpleModal({
              type: 'error',
              title: '이미지 사이즈',
              message: `${width}*${height} 사이즈만 등록 가능합니다.`,
            });
          }

          /**
           *  조건에 맞지 않았을 경우
           */
          if (isInvalid) {
            clearUploadBtn();
            return false;
          }
        }

        /**
         *  정방향 검사
         *  비율이 1:1인경우에는 서버에서 리사이징함
         */
        const ratio = result.width / result.height;
        let isSquare = false;
        if (ratio === 1) isSquare = true;

        /**
         *  크롭 기능을 사용하는 경우
         */
        if ((cropMode && !isSquare) || forceCrop) {
          setIsCropModalOpen(true);
          setState({
            file: e.target.files[0],
            src: result.url,
          });
        } else {
          /**
           *  크롭 기능을 사용하지 않은 경우
           */
          const index = isNil(indexToUpdate) ? list.length + uploadIndex : indexToUpdate;

          dispatch(
            saveImg({
              setData,
              targetPath: [...targetPath, index],
              info: {
                contentType: file.type,
                fileName: file.name,
                width: targetSize ? targetSize.width : result.width,
                height: targetSize ? targetSize.height : result.height,
              },
              requestData: {
                attachment: [file],
                isProductAttachment,
                resizeSize: targetSize
                  ? {
                      width: targetSize.width,
                      height: targetSize.height,
                    }
                  : null,
              },
            }),
          );
          setState((prev) => ({
            ...prev,
            file: null,
            src: null,
          }));
          setIndexToUpdate(null);
        }
      } catch (e) {
        console.error(e);
        await asyncSimpleModal({
          type: 'error',
          title: '이미지 업로드 실패',
          message: '이미지에 문제가 있어 업로드에 실패하였습니다. 다른 이미지로 다시 시도해주시기 바랍니다.',
        });
      } finally {
        clearUploadBtn();
      }
    });
  };

  /**
   * 이미지 크롭하기
   */
  const cropImage = (file) => {
    const index = isNil(indexToUpdate) ? list.length : indexToUpdate;

    dispatch(
      saveImg({
        setData,
        targetPath: [...targetPath, index],
        info: {
          contentType: file.type,
          fileName: file.name,
          width: targetSize.width,
          height: targetSize.height,
        },
        requestData: {
          attachment: [file],
          resizeSize: {
            width: targetSize.width,
            height: targetSize.height,
          },
          isProductAttachment,
        },
        isSquare: true,
      }),
    );
    setIsCropModalOpen(false);
  };
  /**
   * 이미지 변경하기
   * 변경될 대상의 index 를 state 에 저장을 하고,
   * component 가 rerender 가 되고 난 후에
   * 파일을 선택할 수 있는 파일 브라우저를 띄움
   * (편집을 하는 경우에는 파일브라우저에서 한 개의 파일만 선택할 수 있도록 해야함)
   */
  const updateImg = (data) => {
    if (!isEditable) return;

    if (data.type === PRODUCT_ATTACHMENT_TYPE.VIDEO) {
      dispatch(
        appToggleModal({
          name: 'videoUploadModal',
          data: {
            addVideo: (value) =>
              setData({
                path: [...targetPath, data.index],
                value,
              }),
            url: data.url,
            width: data.width,
            height: data.height,
          },
        }),
      );
    } else {
      setIndexToUpdate(data.index);
      inputRef.current?.click();
    }
  };

  /**
   * 이미지 삭제하기
   */
  const deleteImg = (index) => {
    if (!isEditable) return;

    setData({
      path: targetPath,
      value: filter(list, (item, _index) => _index !== index),
    });
  };
  /**가
   * 이미지 미리보기
   */
  const openImgViewModal = (data) => {
    const targetModalData = {
      name: 'imgViewModal',
      data: {
        url: data.url,
      },
    };

    if (data.type === PRODUCT_ATTACHMENT_TYPE.VIDEO) {
      targetModalData.name = 'videoViewModal';
    }
    dispatch(appToggleModal(targetModalData));
  };

  /**
   * 영상 업로드하기
   */
  const addVideo = () => {
    dispatch(
      appToggleModal({
        name: 'videoUploadModal',
        data: {
          addVideo: (value) => {
            setData({
              path: [...targetPath, list.length],
              value,
            });
          },
        },
      }),
    );
  };

  /**
   * 파일의 업로드 취소 시 타겟인덱스가 초기화될 수 있도록 이벤트 핸들러추가
   */
  const handleClickFileInput = () => {
    const handleDialogClose = () => {
      if (inputRef.current && inputRef.current.value === '') {
        // 파일이 선택되지 않았다면 indexToUpdate를 초기화
        setIndexToUpdate(null);
      }
      document.removeEventListener('focus', handleDialogClose, true);
    };
    document.addEventListener('focus', handleDialogClose, true);
  };

  return (
    <div className="img-upload">
      {/* 크롭 모달 */}
      <Modal
        title={modalTitle}
        onClose={() => setIsCropModalOpen(false)}
        isOpen={isCropModalOpen}
        width={650}
        height={520}
        maskClosable={false}
        isFooter={true}
        okText="확인"
        cancelText="취소"
        onOk={() => cropperRef.current.handleClickCrop()}
      >
        <div className="img-cropper-modal">
          {isCropModalOpen && (
            <ReactCropper
              rotatable={rotatable}
              uploadCroppedFile={cropImage}
              ref={cropperRef}
              files={state.file}
              openImgViewModal={openImgViewModal}
            />
          )}
        </div>
      </Modal>

      <div className="img-container">
        <div className="wrap-img">
          <DragSortListWrap
            onDragEnd={onDragEnd}
            list={list}
            item={(data, index) => (
              <div className="img">
                {data.type === PRODUCT_ATTACHMENT_TYPE.VIDEO ? (
                  <ReactPlayer className="react-player" width={140} height={140} light={true} url={data.url} />
                ) : (
                  <Img clickAble={false} effect src={data.url} />
                )}

                {list.length > 1 ? (
                  <p className="orderNumber">
                    {/* 번호 */}
                    <Badge className="badge" color="primary" badgeContent={index + 1} />
                  </p>
                ) : null}

                <p className="toolbar">
                  {/* 미리보기 */}
                  <Anchor
                    onClick={() =>
                      openImgViewModal({
                        url: data.url,
                        type: data.type,
                        width: data.width,
                        height: data.height,
                      })
                    }
                    text={<SearchOutlined />}
                  />

                  {isEditable && (
                    <>
                      {/* 변경하기 */}
                      <Anchor
                        onClick={() =>
                          updateImg({
                            index,
                            url: data.url,
                            type: data.type,
                            width: data.width,
                            height: data.height,
                          })
                        }
                        text={<EditOutlined />}
                      />

                      {/* 삭제하기 */}
                      <Anchor onClick={() => deleteImg(index)} text={<CloseOutlined />} />
                    </>
                  )}
                </p>
              </div>
            )}
          />

          {/* 이미지 추가 */}
          {!isCropModalOpen && isEditable && (
            <div
              className={cx('register-img-wrap', {
                hide: list.length >= maxCnt,
              })}
            >
              <div className="register-img">
                <a className="add-btn">
                  {canAddVideo || onlyVideo ? (
                    <Fragment>
                      {!onlyVideo && (
                        <div className="add-img-btn">
                          <AiOutlineFileImage />
                        </div>
                      )}
                      <div
                        className={cx('add-video-btn', {
                          'only-video': onlyVideo,
                        })}
                        onClick={addVideo}
                      >
                        <AiOutlineVideoCamera />
                      </div>
                    </Fragment>
                  ) : (
                    <PlusOutlined />
                  )}
                </a>
                {!onlyVideo && (
                  <input
                    type="file"
                    className={cx('file-input', {
                      'can-add-video': canAddVideo,
                    })}
                    accept="image/*"
                    multiple={multiple && isNil(indexToUpdate)}
                    ref={inputRef}
                    onChange={onChange}
                    onClick={handleClickFileInput}
                  />
                )}
              </div>
              {!hideComponentError && minCnt && minCnt > list.length ? (
                <div className="form-explain">필수 정보입니다.</div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImgUpload;
